import { useRef, useState, useEffect } from 'react';
import { Box, Button, Typography, useTheme, Dialog } from '@mui/material';
import { CommentType, PostType } from '../memeverse_types';
import FeedTopbar from './FeedTopbar';
import FeedComponent from './FeedComponent';
import { VirtualizedFeedHandle } from './VirtualizedFeed';
import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import { AblyWebsocketChannelProvider } from '../../../component/AblyWebsockets/AblyWebsocketChannelProvider';
import { useAuth } from '../../../hooks/useAuth';
import { useLocation, useNavigate } from 'react-router-dom';
import MetaTags from '../../../component/MetaTags';
import ViewCommunityPage from '../../../svg/ViewCommunityPage.png';
import CreateUpdateComment from '../CreateUpdateComment';
import { useChannel } from 'ably/react';
import SwipeableBox from '../../../component/SwipeableBox';
import { RefreshStatesProvider } from './RefreshStatesContext';
import Footer from '../../footer';

const Feed = ({ startingTabValue = 0 }) => {
  return (
    <AblyWebsocketChannelProvider channelName='ask'>
      <MainFeed startingTabValue={startingTabValue} />
    </AblyWebsocketChannelProvider>
  );
};

const MainFeed = ({ startingTabValue = 0 }) => {
  const scrollBoxRef = useRef(null);
  const feedRef = useRef<VirtualizedFeedHandle>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [tabValue, setTabValue] = useState(
    location?.state?.tabValue || startingTabValue || 0
  );
  const { authUser } = useAuth();
  const theme = useTheme();
  const [replyingTo, setReplyingTo] = useState<PostType | CommentType | null>(
    null
  );
  const [isFollowingEmpty, setIsFollowingEmpty] = useState<boolean>(false);

  const { publish } = useChannel('ask');

  useEffect(() => {
    if (replyingTo) {
      publish('comment.thinking.new', {
        userId: authUser?.uid,
        parentId: replyingTo.docId,
        level: 'comments_counts' in replyingTo ? 0 : 1,
        userType: 'user',
        user_details: {
          userName: authUser?.userName,
          profilePicUrl: authUser?.profilePicUrl,
          fullName: authUser?.fullName,
          user_id: authUser?.uid,
        },
      });
    }
    return () => {
      if (replyingTo) {
        publish('comment.thinking.remove', {
          userId: authUser?.uid,
          parentId: replyingTo.docId,
          level: 'level' in replyingTo ? replyingTo.level + 1 : 0,
          userType: 'user',
        });
      }
    };
  }, [replyingTo, authUser, publish]);

  const handleTabChange = (_: any, newValue: any) => {
    if (newValue < 0) newValue = 0;
    if (newValue > 2) newValue = 2;
    if (!authUser && newValue > 1) newValue = 1;

    if (newValue === 0) {
      navigate('/headlines', { state: { scrollY: 0, tabValue: newValue } });
    } else if (newValue === 1) {
      navigate('/ask', { state: { scrollY: 0, tabValue: newValue } });
    } else {
      setTabValue(newValue);
    }
  };

  useEffect(() => {
    scrollBoxRef.current?.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant',
    });
  }, [tabValue]);

  useEffect(() => {
    if (location.state?.scrollY) {
      scrollBoxRef.current?.scrollTo({
        top: location.state?.scrollY,
        left: 0,
        behavior: 'instant',
      });
    }
    if (
      location.state?.tabValue !== undefined &&
      location.state?.tabValue !== tabValue
    ) {
      setTabValue(location.state?.tabValue);
    }
  }, [location]);

  useEffect(() => {
    const rootElement = document.getElementById('app-main');
    if (rootElement) {
      const originalOverflow = rootElement.style.overflow;
      rootElement.style.overflow = 'hidden';
      return () => {
        if (rootElement) {
          rootElement.style.overflow = originalOverflow;
        }
      };
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown') {
        feedRef.current?.scrollToNext();
      } else if (event.key === 'ArrowUp') {
        feedRef.current?.scrollToPrev();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        height: '100%',
        position: 'relative',
      }}
    >
      {tabValue === 0 ? (
        <MetaTags
          title="Expert AI Reactions to Today's Headlines | Soopra"
          description='Stay informed with expert AI personas commenting on trending news stories. Insightful takes, fresh perspectives—updated daily.'
          image='https://soopra.ai/images/soopra_ai_headlines_expert_commentary.png'
        />
      ) : tabValue === 1 ? (
        <MetaTags
          title='Ask AI Personas Anything – Real Insights, Real Experts | Soopra'
          description='Explore a social Q&A feed where AI personas of real experts answer your questions with authentic, personalized insights.'
          image='https://soopra.ai/images/soopra_ask_ai_persona_feed.png'
        />
      ) : (
        <></>
      )}
      <Box
        sx={{
          height: 'auto',
          flexShrink: 0,
          my: { xs: 0, md: 1.25 },
          boxShadow: { xs: 'none', md: '0 1px 4px rgba(0, 0, 0, 0.1)' },
          borderRadius: {
            xs: 0,
            md: 3,
          },
          scrollSnapAlign: 'start',
          scrollSnapStop: 'always',
          scrollMarginTop: '10px',
          maxWidth: 'md',
          width: '100%',
        }}
      >
        <FeedTopbar
          tabValue={tabValue}
          handleTabChange={handleTabChange}
          scrollBoxRef={scrollBoxRef}
        />
      </Box>

      <SwipeableBox
        id='ask-scroll-box'
        className={`tabValue-${tabValue}`}
        ref={scrollBoxRef}
        sx={{
          display: 'flex',
          flexGrow: 1,
          scrollSnapType: 'y mandatory',
          scrollBehavior: 'smooth',
          overflow: 'auto',
          width: '100%',
        }}
        onSwipeLeft={() => {
          handleTabChange(null, tabValue + 1);
        }}
        onSwipeRight={() => {
          handleTabChange(null, tabValue - 1);
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            height: '100%',
            maxWidth: 'md',
            margin: '0 auto',
            maxHeight: '100%',
          }}
        >
          {/* Only render the active tab's FeedComponent */}
          {tabValue === 0 && (
            <RefreshStatesProvider>
              <FeedComponent
                feedType='news'
                isActive={true}
                onReplyClick={setReplyingTo}
                onTabChange={handleTabChange}
                scrollBoxRef={scrollBoxRef}
                feedRef={feedRef}
              />
            </RefreshStatesProvider>
          )}

          {tabValue === 1 && (
            <RefreshStatesProvider>
              <FeedComponent
                feedType='forYou'
                isActive={true}
                onReplyClick={setReplyingTo}
                onTabChange={handleTabChange}
                scrollBoxRef={scrollBoxRef}
                feedRef={feedRef}
              />
            </RefreshStatesProvider>
          )}

          {tabValue === 2 && isFollowingEmpty && (
            <Box
              sx={{
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                px: 2,
                gap: 1,
              }}
            >
              <Typography variant='h6'>You are not following anyone</Typography>

              <Typography variant='body1' color='text.secondary'>
                Check out the Community page to see recent posts from experts.
              </Typography>

              <Box
                component='img'
                src={ViewCommunityPage}
                alt='Community illustration'
                sx={{
                  width: {
                    xs: '240px',
                    md: '300px',
                  },
                }}
                mx='auto'
                my='16px'
              />

              <Button
                variant='contained'
                onClick={() => handleTabChange(null, 1)}
              >
                View Community Page
              </Button>
            </Box>
          )}

          {tabValue === 2 && !isFollowingEmpty && (
            <RefreshStatesProvider>
              <FeedComponent
                feedType='following'
                isActive={true}
                onReplyClick={setReplyingTo}
                onTabChange={handleTabChange}
                scrollBoxRef={scrollBoxRef}
                onEmptyStateChange={setIsFollowingEmpty}
                feedRef={feedRef}
              />
            </RefreshStatesProvider>
          )}
        </Box>

        <Box
          sx={{
            width: '48px',
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            position: 'fixed',
            right: 24,
            top: 0,
            bottom: 0,
          }}
        >
          <Button
            variant='outlined'
            color='primary'
            size='small'
            onClick={() => feedRef.current?.scrollToPrev()}
            sx={{ minWidth: '0px' }}
          >
            <ArrowUpward />
          </Button>
          <Button
            variant='outlined'
            color='primary'
            size='small'
            onClick={() => feedRef.current?.scrollToNext()}
            sx={{ minWidth: '0px' }}
          >
            <ArrowDownward />
          </Button>
        </Box>
      </SwipeableBox>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
        }}
      >
        <Footer />
      </Box>

      {replyingTo && (
        <Dialog
          open={!!replyingTo}
          onClose={() => setReplyingTo(null)}
          fullWidth
          maxWidth='md'
          slotProps={{
            backdrop: {
              sx: {
                display: { xs: 'none', md: 'block' },
              },
            },
            paper: {
              sx: {
                m: 0,
                [theme.breakpoints.down('md')]: {
                  width: '100%',
                  maxWidth: '100%',
                  height: 'calc(100% - 107px)',
                  maxHeight: 'none',
                  boxShadow: 'none',
                  mt: '50px',
                },
              },
            },
          }}
          sx={{
            [theme.breakpoints.down('md')]: {
              '& .MuiDialog-container': {
                alignItems: 'flex-start',
              },
            },
          }}
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <CreateUpdateComment
            setCreateUpdateCommentVisible={() => setReplyingTo(null)}
            parentPostOrComment={replyingTo}
          />
        </Dialog>
      )}
    </Box>
  );
};

export default Feed;